import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Button, Box } from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import PageContainer from 'src/components/container/PageContainer';
import { useMain } from '../hooks/useMain';
import "./PaySlip.css";


// Alternative simpler version for numbers up to 99,99,999
const convertToIndianWords = (num) => {
  if (num === 0 || !num) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  let words = '';

  // Lakhs part
  if (num >= 100000) {
    const lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      if (lakhs >= 10 && lakhs <= 19) {
        words += teens[lakhs - 10] + ' Lakh ';
      } else if (lakhs >= 20) {
        words += tens[Math.floor(lakhs / 10)] + ' ';
        if (lakhs % 10 > 0) {
          words += ones[lakhs % 10] + ' ';
        }
        words += 'Lakh ';
      } else {
        words += ones[lakhs] + ' Lakh ';
      }
      num %= 100000;
    }
  }

  // Thousands part
  if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      if (thousands >= 10 && thousands <= 19) {
        words += teens[thousands - 10] + ' Thousand ';
      } else if (thousands >= 20) {
        words += tens[Math.floor(thousands / 10)] + ' ';
        if (thousands % 10 > 0) {
          words += ones[thousands % 10] + ' ';
        }
        words += 'Thousand ';
      } else {
        words += ones[thousands] + ' Thousand ';
      }
      num %= 1000;
    }
  }

  // Hundreds part
  if (num >= 100) {
    const hundreds = Math.floor(num / 100);
    words += ones[hundreds] + ' Hundred ';
    num %= 100;
  }

  // Tens and Ones part
  if (num > 0) {
    if (num >= 10 && num <= 19) {
      words += teens[num - 10] + ' ';
    } else {
      const tensPlace = Math.floor(num / 10);
      const onesPlace = num % 10;
      if (tensPlace > 0) {
        words += tens[tensPlace] + ' ';
      }
      if (onesPlace > 0) {
        words += ones[onesPlace] + ' ';
      }
    }
  }

  return words.trim();
};

const SalarySlipPdf = () => {
  const componentRef = useRef();
  const location = useLocation();
  const { state } = location;
  const { staff, salarySlip } = state || {};
  const { branchData } = useMain();

  const getTotalDaysInMonth = (paymentMonth) => {
    if (!paymentMonth) return 30;
    const [year, month] = paymentMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  const totalDays = getTotalDaysInMonth(salarySlip?.paymentMonth);

  const calculateSalary = (salary) => {
    const perDaySalary = salary?.basicSalary ? (salary.basicSalary / totalDays) : 0;
    const absentDeduction = Math.floor(perDaySalary * (salary?.monthlyAbsent || 0));
    // monthlyHalfDay is already pre-halved by the backend (e.g. 1 half day = 0.5),
    // so just multiply by perDaySalary directly — no extra /2 here
    const halfDayDeduction = Math.floor(perDaySalary * (salary?.monthlyHalfDay || 0));
    const totalDeductions = Math.floor(absentDeduction + halfDayDeduction + (salary?.expenseAmount || 0));
    const totalEarnings = Math.floor((salary?.basicSalary || 0) + (salary?.totalIncentive || 0));
    const netPay = Math.floor(totalEarnings - totalDeductions);
    return { totalEarnings, totalDeductions, netPay, absentDeduction, halfDayDeduction };
  };

  const { totalEarnings, totalDeductions, netPay, absentDeduction, halfDayDeduction } = calculateSalary(salarySlip);

  const printSlipHandler = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Salary_Slip`,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 15mm;
      }
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        background: white;
      }
    `
  });

  const downloadPdfHandler = async () => {
    const input = componentRef.current;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Salary_Slip.pdf`);
  };

  return (
    <PageContainer title="Salary Slip">
      <DashboardCard2 title="Salary Slip">
        <div ref={componentRef} className="pdf-container">
          {salarySlip ? (
            <>
              <div className="header-section">
                <div style={{ textAlign: 'center' }}>
                  <h2>{branchData?.branchName}</h2>
                  <p>Address: {branchData?.address}</p>
                  <p><strong>Contact:</strong> {branchData?.branchPhone}</p>
                </div>
              </div>

              <table className="details-table">
                <tbody>
                  <tr>
                    <td><strong>Employee Name:</strong> {staff?.name}</td>
                    <td><strong>Date of Joining:</strong> {staff?.dateOfJoining}</td>
                  </tr>
                  <tr>
                    <td><strong>Pay Period:</strong> {salarySlip?.paymentMonth}</td>
                    <td><strong>Department:</strong> {staff?.department}</td>
                  </tr>
                  <tr>
                    <td><strong>Worked Days:</strong> {salarySlip?.monthlyPresent + salarySlip?.monthlyHalfDay}</td>
                    <td><strong>Designation:</strong> {staff?.userType || 'NA'}</td>
                  </tr>
                  <tr>
                    <td><strong>Absent:</strong> {salarySlip?.monthlyAbsent}</td>
                    <td><strong>Half Days:</strong> {salarySlip?.monthlyHalfDay}</td>
                  </tr>
                  <tr>
                    <td><strong>Present:</strong> {salarySlip?.monthlyPresent}</td>
                    <td><strong>Week Off:</strong> {salarySlip?.monthlyWeekOffs}</td>
                  </tr>
                  <tr>
                    <td><strong>Holiday:</strong> {salarySlip?.holidayCount}</td>
                    <td><strong>Leave:</strong> {salarySlip?.leaveCount}</td>
                  </tr>
                </tbody>
              </table>

              <table className="salary-table">
                <thead>
                  <tr>
                    <th>Earnings</th>
                    <th>Amount</th>
                    <th>Deductions</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic Salary</td>
                    <td>₹{Math.round(salarySlip?.basicSalary || 0)}</td>
                    <td>Absent</td>
                    <td>₹{absentDeduction}</td>
                  </tr>
                  <tr>
                    <td>Incentive Pay</td>
                    <td>₹{Math.round(salarySlip?.totalIncentive || 0)}</td>
                    <td>Half-Day</td>
                    <td>₹{halfDayDeduction}</td>
                  </tr>
                  <tr>
                    <td></td><td></td>
                    <td>Advance Amount</td>
                    <td>₹{Math.floor(salarySlip?.expenseAmount || 0)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total Earnings</strong></td>
                    <td><strong>₹{totalEarnings}</strong></td>
                    <td><strong>Total Deductions</strong></td>
                    <td><strong>₹{totalDeductions}</strong></td>
                  </tr>
                </tfoot>
              </table>

              <h3 className="net-pay">Net Pay: ₹{netPay}</h3>
              <p className="amount-words">
                <strong>Amount in Words:</strong> {convertToIndianWords(netPay)} Rupees Only
              </p>

              <div className="signature-section">
                <div>
                  <p><strong>Owner Signature</strong></p>
                  <div className="sign-line"></div>
                </div>
                <div>
                  <p><strong>Employee Signature</strong></p>
                  <div className="sign-line"></div>
                </div>
              </div>

              <div className="footer-note">
                Created by <a href="https://yoursalon.in" target="_blank" rel="noopener noreferrer">YourSalon.in</a>
              </div>
            </>
          ) : <p>No salary slip available.</p>}
        </div>

        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="contained" onClick={printSlipHandler} sx={{ margin: '10px' }}>Print</Button>
          <Button variant="contained" onClick={downloadPdfHandler} sx={{ margin: '10px' }}>Download PDF</Button>
        </Box>
      </DashboardCard2>
    </PageContainer>
  );
};

export default SalarySlipPdf;




